import {
	BoundaryAssertionStrength,
	BoundarySourceType,
	boundaryTargetToString,
	ComponentLimitsValidationResultEntryStatus,
	getBoundaryTargetSignalIds,
	isBoundaryTargetRelativeSignals,
	BoundaryLevel as SigmaBoundaryLevel,
} from '@cadstrom/sigma-engine-shared';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { getRouteApi } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import invariant from 'tiny-invariant';

import { useCoreModelContext } from '#components/cad-renderer/context/core-model-context.js';
import { NetChip } from '#components/net-chip/net-chip.js';
import { getCharacteristicSummaryString } from '#components/net-chip/utils.js';
import {
	formatBoundaryValueForTable,
	getBoundaryCharacteristic,
	renderBoundaryDetails,
} from '#utils/boundary-utils.js';
import { isDefined } from '#utils/is-defined.js';
import { AnalysisResultsModuleChip } from '../components/analysis-results-module-chip.js';
import { SubtableToolbar } from '../components/subtable-toolbar.js';
import { PackageStoreLink } from '../package-store-link.js';

import type {
	CharacteristicStateSummaryExternal,
	CharacteristicValue,
	ComponentLimitsValidationInstanceMetadata,
	ComponentLimitsValidationResultEntry,
	IBoundaryDefinitionWithId,
	ModelWithNetStats,
	NumericalBoundary,
	RequirementInstanceResult,
	BoundaryCharacteristic as SigmaBoundaryCharacteristic,
	SignalId,
} from '@cadstrom/sigma-engine-shared';
import type { DataGridPremiumProps, GridColDef, GridRowModel } from '@mui/x-data-grid-premium';

interface NetInfoDisplay {
	net: string;
	characteristic: SigmaBoundaryCharacteristic;
	value: CharacteristicValue;
	summary: string;
}

interface BoundaryTableRow {
	id: string;
	boundaryId: string;
	characteristic: string;
	type: NumericalBoundary['type'] | 'connection';
	value: string;
	actualValue: CharacteristicStateSummaryExternal | undefined;
	relativeValue: CharacteristicStateSummaryExternal | undefined;
	hasRelativeTo: boolean;
	target: string | null;
	relativeTo: string | null;
	signalId: SignalId;
	strength: BoundaryAssertionStrength;
	level: SigmaBoundaryLevel;
	class: string;
	severity: ComponentLimitsValidationResultEntryStatus;
	sourceType: BoundarySourceType;
}

interface ComponentLimitsDetailPanelProps {
	row: GridRowModel<RequirementInstanceResult>;
	loading?: boolean;
	projectModel?: ModelWithNetStats;
	/**
	 * Optional click-through for module ref-des chip menus. Omit when the panel
	 * is mounted outside the analysis-results grid (e.g. inside a V2 activity
	 * timeline payload) — the menu item is dropped in that case.
	 */
	handleAddModuleFilter?: (moduleReferenceDesignator: string) => void;
}

function findNetInfo(
	netName: string,
	netValues: ComponentLimitsValidationResultEntry['netValues'],
): NetInfoDisplay | null {
	const netValue = netValues.find((nv) => nv.net === netName);
	if (!netValue?.characteristics || netValue.characteristics.length === 0) {
		return null;
	}

	const char = netValue.characteristics[0];
	if (!char) {
		return null;
	}

	return {
		net: netValue.net,
		characteristic: char.characteristic,
		value: char.value,
		summary: getCharacteristicSummaryString(char.characteristic, char.value),
	};
}

function BoundariesTableToolbar({
	boundariesLength,
	referenceDesignator,
}: {
	boundariesLength: number;
	referenceDesignator: string;
}) {
	return <SubtableToolbar subtitle={`${boundariesLength} boundaries`} referenceDesignator={referenceDesignator} />;
}

function BoundariesTableContent({
	row,
	loading,
	handleAddModuleFilter,
}: {
	row: RequirementInstanceResult;
	loading: boolean;
	handleAddModuleFilter?: (moduleReferenceDesignator: string) => void;
}) {
	const { coreModel } = useCoreModelContext(true);

	const instanceMetadata =
		row.instanceMetadata?.type === 'ComponentLimitValidationResult' ? row.instanceMetadata : null;
	const resultMetadata = row.resultMetadata?.type === 'ComponentLimitValidationResult' ? row.resultMetadata : null;

	const referenceDesignator = instanceMetadata?.referenceDesignator ?? '';
	invariant(referenceDesignator, 'Reference designator is required');

	const [projectModule, component] = useMemo(() => {
		const projectModule = coreModel.modules.find((m) => m.name === referenceDesignator);
		if (!projectModule) {
			return [];
		}

		return [projectModule, coreModel.components[projectModule.componentRef]];
	}, [coreModel, referenceDesignator]);

	invariant(projectModule, `Could not identify project module for ${referenceDesignator}`);
	invariant(component, `Could not identify component for ${referenceDesignator}`);
	invariant(resultMetadata, 'Result metadata is required');

	// Extract boundaries from instance metadata
	const boundaries = useMemo(() => {
		if (instanceMetadata) {
			return instanceMetadata.boundaries;
		}
		return [];
	}, [instanceMetadata]);

	const netValues = useMemo(() => {
		if (resultMetadata) {
			return resultMetadata.results.flatMap((r) => r.netValues);
		}
		return [];
	}, [resultMetadata]);

	// Transform boundaries into table rows
	const boundaryRows = useMemo((): BoundaryTableRow[] => {
		if (boundaries.length === 0) {
			return [];
		}

		const dedupedBoundaries = new Map<string, IBoundaryDefinitionWithId>();
		for (const b of boundaries) {
			const id = b.boundaryGlobalId ?? b.boundaryId;
			if (!id.includes('::modes::')) {
				dedupedBoundaries.set(id, b);
			}
		}

		return Array.from(dedupedBoundaries.values()).flatMap((boundary, index: number) => {
			const resultForThisBoundary = resultMetadata.results.find((r) =>
				r.errors.find((e) => e.boundary.id === boundary.boundaryId),
			);
			const characteristic = getBoundaryCharacteristic(boundary);
			const value = formatBoundaryValueForTable(boundary);

			// Extract the actual boundary type based on the boundary structure
			const type: NumericalBoundary['type'] | 'connection' =
				boundary.type === 'numerical' ? boundary.numerical.type : 'connection';

			const strength = boundary.strength ?? BoundaryAssertionStrength.None;
			const level = boundary.level ?? SigmaBoundaryLevel.Error;
			const sourceType =
				boundary.type === 'numerical' ?
					(boundary.sourceType ?? BoundarySourceType.Permanent)
				:	BoundarySourceType.Permanent;

			if (!isBoundaryTargetRelativeSignals(boundary.target)) {
				return [];
			}

			const targetSignalIds = getBoundaryTargetSignalIds(component.signals, boundary.target);
			const relativeSignalId = getBoundaryTargetSignalIds(component.signals, boundary.relative_to)[0];
			const relativeSignal = component.signals.find((s) => s.id === relativeSignalId);

			const relativeNet = (
				Array.from(
					new Set(
						projectModule.instances.flatMap((i) => {
							const design = coreModel.designs[i.designId];
							if (design?.modelType !== 'schematic') {
								return [];
							}

							const designModule = design.modules.find((m) => m.ref === i.ref);
							if (!designModule) {
								return [];
							}

							const pins =
								relativeSignal?.[design.modelType === 'schematic' ? 'pinRefs' : 'padRefs']
									.filter((s) => s !== null && s in designModule.pinNets)
									.filter((s) => s !== null) ?? [];
							for (const pin of pins) {
								if (pin in designModule.pinNets && designModule.pinNets[pin] !== null) {
									return [designModule.pinNets[pin]];
								}
							}
						}),
					),
				) as string[]
			)[0];

			const relativeNetValue = netValues
				.map(
					(nv) =>
						nv.net === relativeNet && nv.characteristics.filter((c) => (c.characteristic as string) === characteristic),
				)
				.filter((v) => v !== false)
				.flat()[0];

			return targetSignalIds.map((targetSignalId) => {
				const signal = component.signals.find((s) => s.id === targetSignalId);

				const targetNets = Array.from(
					new Set(
						projectModule.instances.flatMap((i) => {
							const design = coreModel.designs[i.designId];
							if (design?.modelType !== 'schematic') {
								// console.log('design not schematic', design);
								return [];
							}

							const designModule = design.modules.find((m) => m.ref === i.ref);
							if (!designModule) {
								// console.log('design module not found', i.ref);
								return [];
							}

							const pins =
								signal?.[design.modelType === 'schematic' ? 'pinRefs' : 'padRefs']
									.filter((s) => s !== null && s in designModule.pinNets)
									.filter((s) => s !== null) ?? [];
							// console.log(referenceDesignator, 'pins', pins, designModule.pinNets);
							for (const pin of pins) {
								if (pin in designModule.pinNets && designModule.pinNets[pin] !== null) {
									return [designModule.pinNets[pin]];
								}
							}
						}),
					),
				) as string[];

				const targetNet = targetNets[0];
				const targetNetValue = netValues
					.map(
						(nv) =>
							nv.net === targetNet && nv.characteristics.filter((c) => (c.characteristic as string) === characteristic),
					)
					.filter((v) => v !== false)
					.flat()[0];

				const hasRelativeTo = boundary.relative_to !== undefined;

				// Severity is computed per-row from data on this boundary:
				// - FAIL: BoundaryChecker emitted an error for this boundary.
				// - UNKNOWN_NET_STATES: the target net (or, for relative boundaries, the relative
				//   reference net) has no resolved characteristic state, so BoundaryChecker silently
				//   skipped the boundary without producing an error.
				// - PASS: no error was emitted and the required net data is present. A passing
				//   boundary produces no output of its own, so PASS is the inferred default once
				//   FAIL and UNKNOWN have been ruled out.
				let severity: ComponentLimitsValidationResultEntryStatus;
				if (resultForThisBoundary !== undefined) {
					severity = ComponentLimitsValidationResultEntryStatus.FAIL;
				} else if (!targetNetValue || (hasRelativeTo && !relativeNetValue)) {
					severity = ComponentLimitsValidationResultEntryStatus.UNKNOWN_NET_STATES;
				} else {
					severity = ComponentLimitsValidationResultEntryStatus.PASS;
				}

				return {
					id: `boundary-${boundary.boundaryGlobalId ?? boundary.boundaryId ?? index}`,
					boundaryId: boundary.boundaryId,
					characteristic,
					class: boundary.class ?? '',
					type,
					value,
					actualValue: targetNetValue,
					relativeValue: relativeNetValue,
					hasRelativeTo,
					relativeTo: relativeNet ?? null, // Relative to info not available in instanceMetadata boundaries
					target: targetNet ?? null, // Target info not available in instanceMetadata boundaries
					strength,
					severity,
					level,
					sourceType,
					signalId: targetSignalId,
				};
			});
		});
	}, [
		boundaries,
		resultMetadata.results,
		component.signals,
		projectModule.instances,
		netValues,
		coreModel.designs,
		referenceDesignator,
	]);

	const boundariesTableColumns: GridColDef<BoundaryTableRow>[] = [
		{
			field: 'signalId',
			headerName: 'Signal',
			flex: 1,
			minWidth: 120,
			renderCell: (params) => {
				return (
					<AnalysisResultsModuleChip
						moduleReferenceDesignator={referenceDesignator}
						signalId={params.row.signalId}
						handleAddModuleFilter={handleAddModuleFilter}
					/>
				);
			},
		},
		{
			field: 'target',
			headerName: 'Target Net',
			flex: 1,
			minWidth: 120,
			renderCell: (params) => {
				if (params.row.target === null) {
					return 'N/A';
				}
				return <NetChip net={params.row.target} />;
			},
		},
		{
			field: 'relativeTo',
			headerName: 'Relative To Net',
			flex: 1,
			minWidth: 120,
			renderCell: (params) => {
				if (params.row.relativeTo === null) {
					return 'N/A';
				}
				return <NetChip net={params.row.relativeTo} />;
			},
		},
		{ field: 'boundaryId', headerName: 'Boundary ID', flex: 1, minWidth: 120 },
		{ field: 'class', headerName: 'Class', flex: 1, minWidth: 120 },
		{
			field: 'characteristic',
			headerName: 'Characteristic',
			flex: 1,
			minWidth: 120,
		},
		{
			field: 'type',
			headerName: 'Type',
			flex: 1,
			minWidth: 100,
		},
		{
			field: 'value',
			headerName: 'Value',
			flex: 1,
			minWidth: 150,
		},
		{
			field: 'actualValue',
			headerName: 'Actual Value',
			flex: 1,
			minWidth: 150,
			renderCell: (params) => {
				const val = params.row.actualValue?.value;
				const relativeVal = params.row.relativeValue?.value;
				// When the boundary is relative but the reference net has no resolved state, we cannot
				// compute a meaningful comparison. Render dashes to avoid misleading data.
				const relativeUnresolved = params.row.hasRelativeTo && !params.row.relativeValue;

				let min: string;
				let max: string;
				let value: string;
				if (relativeUnresolved) {
					min = '-';
					max = '-';
					value = '-';
				} else {
					// For non-relative boundaries, relativeVal is undefined and the `?? 0` fallbacks below
					// reduce the subtraction to a no-op, so the raw target value is shown.
					min =
						typeof val?.allowableRange.min === 'number' ?
							(
								val.allowableRange.min -
								(relativeVal?.allowableRange.min ?? relativeVal?.value ?? relativeVal?.allowableRange.max ?? 0)
							).toPrecision(3)
						:	'-';
					max =
						typeof val?.allowableRange.max === 'number' ?
							(
								val.allowableRange.max -
								(relativeVal?.allowableRange.max ?? relativeVal?.value ?? relativeVal?.allowableRange.min ?? 0)
							).toPrecision(3)
						:	'-';
					value = val?.value?.toPrecision(3) ?? '-';
				}
				return (
					<Tooltip
						title={
							<div className='flex flex-col gap-2'>
								<p className='mb-1 text-gray-a11'>
									Target Net Value: {val?.allowableRange.min ?? '-'} ≤ {val?.value ?? '-'} ≤{' '}
									{val?.allowableRange.max ?? '-'}
								</p>
								<p className='mb-1 text-gray-a11'>
									Relative Net Value: {relativeVal?.allowableRange.min ?? '-'} ≤ {relativeVal?.value ?? '-'} ≤{' '}
									{relativeVal?.allowableRange.max ?? '-'}
								</p>
								{relativeUnresolved && (
									<p className='mb-1 text-gray-a11'>No state available for the relative reference net.</p>
								)}
							</div>
						}
					>
						<span>
							{min} ≤ {value} ≤ {max}
						</span>
					</Tooltip>
				);
			},
		},
		// {
		// 	field: 'strength',
		// 	headerName: 'Strength',
		// 	flex: 1,
		// 	minWidth: 100,
		// },
		{
			field: 'level',
			headerName: 'Level',
			flex: 1,
			minWidth: 100,
		},
		{
			field: 'severity',
			headerName: 'Severity',
			flex: 1,
			minWidth: 100,
			renderCell: (params) => {
				const severity: ComponentLimitsValidationResultEntryStatus =
					(params.value as ComponentLimitsValidationResultEntryStatus) ??
					ComponentLimitsValidationResultEntryStatus.PASS;

				let labelString: string;
				let color: 'success' | 'warning' | 'error' | 'orange';
				switch (severity) {
					case ComponentLimitsValidationResultEntryStatus.PASS: {
						labelString = 'PASS';
						color = 'success';
						break;
					}
					case ComponentLimitsValidationResultEntryStatus.ERROR: {
						labelString = 'ERROR';
						color = 'error';
						break;
					}
					case ComponentLimitsValidationResultEntryStatus.UNKNOWN_NET_STATES: {
						labelString = 'UNKNOWN';
						color = 'orange';
						break;
					}
					case ComponentLimitsValidationResultEntryStatus.FAIL: {
						labelString = 'FAIL';
						color = params.row.level === SigmaBoundaryLevel.Error ? 'error' : 'warning';
						break;
					}
				}

				return <Chip label={labelString} size='small' color={color} variant='filled' />;
			},
		} as GridColDef<BoundaryTableRow, string>,
		// {
		// 	field: 'sourceType',
		// 	headerName: 'Source Type',
		// 	flex: 1,
		// 	minWidth: 120,
		// },
	];

	const slots: DataGridPremiumProps['slots'] = useMemo(
		() => ({
			// eslint-disable-next-line react/no-unstable-nested-components
			toolbar: () => (
				<BoundariesTableToolbar boundariesLength={boundaryRows.length} referenceDesignator={referenceDesignator} />
			),
		}),
		[boundaryRows.length, referenceDesignator],
	);

	if (boundaryRows.length === 0) {
		return (
			<div className='py-4 text-center'>
				<Typography className='text-gray-a9'>No boundaries available for this component</Typography>
			</div>
		);
	}

	return (
		<DataGridPremium
			hideFooter
			loading={loading && boundaryRows.length === 0}
			disableColumnMenu
			rows={boundaryRows}
			columns={boundariesTableColumns}
			disableRowSelectionOnClick
			scrollEndThreshold={24}
			getRowHeight={() => 30}
			showToolbar
			slots={slots}
			initialState={{
				columns: {
					columnVisibilityModel: {
						boundaryId: false,
						characteristic: false,
						type: false,
						level: false,
					},
				},
			}}
			sx={{ height: Math.min(400, (boundaryRows.length + 1) * 30 + 56) }} // Auto-height replacement
		/>
	);
}

function NetValuesContent({ netValues }: { netValues: ComponentLimitsValidationResultEntry['netValues'] }) {
	if (netValues.length === 0) {
		return (
			<div className='py-4 text-center'>
				<Typography className='text-gray-a9'>No net values available for this component</Typography>
			</div>
		);
	}

	return (
		<div className='space-y-1 pt-1'>
			<div className='grid gap-2'>
				{netValues.map((netValue) => {
					const hasCharacteristics = netValue.characteristics.length > 0;

					return (
						<div key={netValue.net} className='rounded-lg border border-gray-a6 bg-blue-2 p-3'>
							<NetChip net={netValue.net} />

							{hasCharacteristics ?
								<div className='space-y-1'>
									{netValue.characteristics.map((char) => {
										return (
											<div key={`${netValue.net}-${char.characteristic}`} className='border-l-2 border-gray-a6 pl-3'>
												<div className='space-y-1'>
													<Typography className='text-sm font-medium text-gray-a11'>{char.characteristic}</Typography>
													<div className='space-y-1 text-sm text-gray-a10'>
														<div>
															<span className='font-medium'>Value:</span>{' '}
															{getCharacteristicSummaryString(char.characteristic, char.value)}
														</div>
														{(char.value.relativeToNet?.length ?? 0) > 0 && (
															<div>
																<span className='font-medium'>Relative to:</span> {char.value.relativeToNet}
															</div>
														)}
													</div>
												</div>
											</div>
										);
									})}
								</div>
							:	<Typography className='pl-4 text-sm text-gray-a9'>No characteristics available</Typography>}
						</div>
					);
				})}
			</div>
		</div>
	);
}

const routeApi = getRouteApi('/_authorization-gate/orgs/$orgName/projects/$projectId/revisions/$revisionId/results');

export function ComponentLimitsDetailPanel({
	row,
	loading = false,
	projectModel: _projectModel,
	handleAddModuleFilter,
}: ComponentLimitsDetailPanelProps) {
	const [accordionExpanded, setAccordionExpanded] = useState<string | false>(false);
	const { projectId, revisionId: buildId } = routeApi.useParams();

	// Type guard check - if wrong metadata type, we'll return null at the end
	const instanceMetadata =
		row.instanceMetadata?.type === 'ComponentLimitValidationResult' ? row.instanceMetadata : null;

	const resultMetadata = row.resultMetadata?.type === 'ComponentLimitValidationResult' ? row.resultMetadata : null;

	const results = useMemo((): ComponentLimitsValidationResultEntry[] => {
		if (resultMetadata) {
			return resultMetadata.results;
		}
		return [];
	}, [resultMetadata]);

	const detailRows = useMemo((): ComponentLimitsValidationResultEntry[] => {
		if (results.length === 0) {
			return [];
		}

		return results;
	}, [results]);

	const referenceDesignator = instanceMetadata?.referenceDesignator ?? '';

	// Detail panel content for expandable rows
	const getDetailPanelContent = ({ row: detailRow }: { row: ComponentLimitsValidationResultEntry }) => {
		return (
			<Box className='w-full space-y-2'>
				{/* Reference Designator Heading */}
				<div className='px-2 pt-2'>
					<AnalysisResultsModuleChip
						moduleReferenceDesignator={referenceDesignator}
						handleAddModuleFilter={handleAddModuleFilter}
					/>
				</div>

				{/* Error Details Section */}
				{detailRow.errors.length > 0 && (
					<div className='grid gap-2'>
						{detailRow.errors.map((error, index: number) => {
							// Provide fallbacks for missing fields
							const errorId = error.errorId || 'Unknown Error';
							const message = error.message || 'No message available';
							const displayText = errorId && message ? `${errorId}: ${message}` : errorId || message;

							let targetSignal = '';
							try {
								if (isDefined(error.boundary.boundary) && 'target' in error.boundary.boundary) {
									targetSignal = boundaryTargetToString(error.boundary.boundary.target);
								}
							} catch {}

							const targetNetInfo =
								error.targetNet !== null && error.targetNet !== undefined ?
									findNetInfo(error.targetNet, detailRow.netValues)
								:	null;
							const relativeNetInfo =
								error.relativeNet !== null && error.relativeNet !== undefined ?
									findNetInfo(error.relativeNet, detailRow.netValues)
								:	null;

							return (
								<div key={error.id || `error-${index}`} className='rounded-lg border border-gray-a6 bg-blue-2 p-2'>
									<div className='space-y-2'>
										<div className='flex items-start justify-between gap-3'>
											<Typography className='flex-1 font-medium text-gray-a12'>{displayText}</Typography>
											<Chip
												label={error.level}
												size='small'
												color={error.level === SigmaBoundaryLevel.Error ? 'error' : 'warning'}
												variant='filled'
												className='shrink-0'
											/>
										</div>

										{/* Display target signal if available */}
										{targetSignal && (
											<div className='flex items-center gap-2'>
												<Typography className='text-sm font-medium text-gray-a11'>Target Signal:</Typography>
												<PackageStoreLink
													projectId={projectId}
													buildId={buildId}
													referenceDesignator={referenceDesignator}
													displayText={targetSignal}
													toTab='signal-drive-modes'
													className='font-mono text-sm'
												/>
											</div>
										)}

										<div className='mt-3 space-y-2 rounded-md bg-gray-a1 p-3'>
											<Typography className='text-sm font-medium text-gray-a11'>
												<span className='text-purple-a11'>Boundary Information:</span>
											</Typography>
											<div className='space-y-1 pl-2'>
												{error.boundary.globalId && (
													<Typography className='text-xs text-gray-a10'>
														<span className='font-medium'>ID:</span> {error.boundary.globalId}
													</Typography>
												)}
												<div className='space-y-1'>
													{renderBoundaryDetails(error.boundary.boundary).map((field) => (
														<Typography key={field.label} className='text-xs text-gray-a10'>
															<span className='font-medium'>{field.label}:</span> {field.value}
														</Typography>
													))}
												</div>
											</div>
										</div>

										{error.targetNet !== null && error.targetNet !== '' && error.targetNet !== undefined && (
											<div className='mt-3 space-y-3 rounded-md bg-gray-a1 pl-2'>
												<div className='space-y-2'>
													<Typography className='text-sm font-medium text-gray-a11'>
														<span className='text-blue-a11'>Target Net:</span> <NetChip net={error.targetNet} />
													</Typography>
													{error.relativeNet !== null &&
														error.relativeNet !== '' &&
														error.relativeNet !== undefined && (
															<Typography className='text-sm font-medium text-gray-a11'>
																<span className='text-blue-a11'>Relative to:</span> <NetChip net={error.relativeNet} />
															</Typography>
														)}
												</div>

												{targetNetInfo && (
													<div className='border-l-4 border-blue-a6 pl-3'>
														<Typography className='text-sm text-gray-a12'>
															<span className='font-semibold text-blue-a11'>{targetNetInfo.characteristic}</span>
														</Typography>
														<Typography className='mt-2 font-mono text-sm text-gray-a11'>
															{targetNetInfo.summary}
														</Typography>
													</div>
												)}

												{relativeNetInfo && (
													<div className='border-l-4 border-orange-a6 pl-3'>
														<Typography className='text-sm text-gray-a12'>
															<span className='font-semibold text-orange-a11'>
																<NetChip net={relativeNetInfo.net} /> {relativeNetInfo.characteristic}
															</span>
														</Typography>
														<Typography className='mt-2 font-mono text-sm text-gray-a11'>
															{relativeNetInfo.summary}
														</Typography>
													</div>
												)}
											</div>
										)}
									</div>
								</div>
							);
						})}
					</div>
				)}

				{(detailRow.netValues.length > 0 ||
					(row.instanceMetadata as ComponentLimitsValidationInstanceMetadata).boundaries.length > 0) && (
					<div>
						<Accordion
							expanded={accordionExpanded === 'netValues'}
							onChange={() => setAccordionExpanded(accordionExpanded === 'netValues' ? false : 'netValues')}
							sx={{
								'&:not(:last-child)': {
									borderBottom: 0,
								},
								'&::before': {
									display: 'none',
								},
								margin: 0,
								'&.Mui-expanded': {
									margin: 0,
								},
							}}
						>
							<AccordionSummary
								expandIcon={<ExpandMoreIcon />}
								sx={{
									minHeight: '20px',
									'&.Mui-expanded': {
										minHeight: '20px',
									},
									'& .MuiAccordionSummary-content': {
										margin: '8px 0',
										'&.Mui-expanded': {
											margin: '8px 0',
										},
									},
								}}
							>
								<Typography className='font-semibold'>
									Net Values & Characteristics ({detailRow.netValues.length})
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<NetValuesContent netValues={detailRow.netValues} />
							</AccordionDetails>
						</Accordion>
					</div>
				)}

				{/* Show message when no data is available */}
				{detailRow.errors.length === 0 && detailRow.netValues.length === 0 && (
					<div className='py-4 text-center'>
						<Typography className='text-gray-a9'>No detailed information available for this component</Typography>
					</div>
				)}
			</Box>
		);
	};

	// Early return if wrong metadata type (after all hooks have been called)
	if (!instanceMetadata || !resultMetadata) {
		return (
			<div className='w-full overflow-auto bg-gray-a5 p-4'>
				<p className='text-gray-a9 italic'>Metadata is not available. This can indicate a data issue.</p>
			</div>
		);
	}

	return (
		<div className='w-full space-y-2 overflow-auto bg-gray-a5'>
			<NewDetailPanelContent row={row} handleAddModuleFilter={handleAddModuleFilter} />
			{detailRows.map((result) => {
				return (
					<div key={`${result.status}-${result.errors.length}-${result.netValues.length}`} className='w-full'>
						{getDetailPanelContent({ row: result })}
					</div>
				);
			})}
		</div>
	);
}

export function NewDetailPanelContent({
	row,
	handleAddModuleFilter,
}: {
	row: RequirementInstanceResult;
	handleAddModuleFilter?: (moduleReferenceDesignator: string) => void;
}) {
	return (
		<div className='overflow-auto bg-gray-a5 px-2'>
			<BoundariesTableContent row={row} loading={false} handleAddModuleFilter={handleAddModuleFilter} />
		</div>
	);
}
